import React from "react";
import { useParams } from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";

// Mirrors VALID_EVENT_TYPES in api-admin/resolvers/integration.js — the server validates again,
// so this list is what a client can SEE, not what they are allowed to save.
const EVENT_TYPES = ["registration", "scan", "print", "agenda.published"];
const SCAN_DIRECTIONS = ["Entry", "Exit", "PRINT"];
const AUTH_METHODS = [
  { value: "bearer", label: "Bearer token" },
  { value: "api_key_header", label: "API key header" },
  { value: "hmac", label: "HMAC signed (recommended)" },
];

const INTEGRATIONS_QUERY = gql`
  query Integrations($eventId: String!) {
    integrations(eventId: $eventId) {
      id
      name
      destinationUrl
      eventTypes
      roles
      scanDirections
      authMethod
      authHeaderName
      signatureAlgo
      hasSecret
      enabled
      maxAttempts
      timeoutMs
      createdAt
      updatedAt
      lastDeliveryStatus
      lastDeliveryAt
    }
    allowedRoles(eventId: $eventId)
  }
`;

const DELIVERIES_QUERY = gql`
  query Deliveries(
    $eventId: String!
    $integrationId: String
    $status: String
    $eventType: String
    $resourceId: String
    $limit: Int
  ) {
    integrationDeliveries(
      eventId: $eventId
      integrationId: $integrationId
      status: $status
      eventType: $eventType
      resourceId: $resourceId
      limit: $limit
    ) {
      id
      integrationId
      eventType
      role
      resourceId
      status
      attempts
      lastStatusCode
      lastError
      lastResponseExcerpt
      payloadJson
      nextAttemptAt
      lastAttemptAt
      deliveredAt
      createdAt
    }
  }
`;

const CREATE = gql`
  mutation Create($eventId: String!, $inp: IntegrationInput!) {
    createIntegration(eventId: $eventId, inp: $inp) {
      id
    }
  }
`;
const UPDATE = gql`
  mutation Update($eventId: String!, $id: String!, $inp: IntegrationInput!) {
    updateIntegration(eventId: $eventId, id: $id, inp: $inp) {
      id
    }
  }
`;
const TOGGLE = gql`
  mutation Toggle($eventId: String!, $id: String!, $enabled: Boolean!) {
    toggleIntegration(eventId: $eventId, id: $id, enabled: $enabled) {
      id
      enabled
    }
  }
`;
const DELETE = gql`
  mutation Delete($eventId: String!, $id: String!) {
    deleteIntegration(eventId: $eventId, id: $id)
  }
`;
const TEST_FIRE = gql`
  mutation Test($eventId: String!, $id: String!) {
    sendIntegrationTestPayload(eventId: $eventId, id: $id) {
      deliveryId
      status
      message
    }
  }
`;
const REPLAY = gql`
  mutation Replay($eventId: String!, $deliveryId: String!) {
    replayIntegrationDelivery(eventId: $eventId, deliveryId: $deliveryId) {
      id
      status
    }
  }
`;

export default function EventIntegrationsPage() {
  const { eventId } = useParams();
  const { data, loading, error, refetch } = useQuery(INTEGRATIONS_QUERY, {
    variables: { eventId },
    pollInterval: 5000,
  });
  const [editingId, setEditingId] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [viewingDeliveriesFor, setViewingDeliveriesFor] = React.useState(null);

  if (loading && !data) return <LinearProgress />;
  if (error)
    return (
      <Alert severity="error">{error.message || "Failed to load"}</Alert>
    );

  const integrations = data?.integrations || [];
  const roles = data?.allowedRoles || [];

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Outbound Integrations
        </Typography>
        <Button
          variant="contained"
          startIcon={<Icon icon="mdi:plus" />}
          onClick={() => setCreating(true)}
        >
          Add integration
        </Button>
      </Stack>

      <Alert severity="info">
        Configure where Eventegration should POST registration / scan / print
        events, and agenda publishes. The originating API records its own row
        and returns success first — webhook delivery happens out-of-band and
        never blocks a registration, a scan, or a publish.
        <br />
        <strong>agenda.published</strong> carries only the version and counts,
        never the programme itself — subscribers refetch the agenda API with
        their ETag, so what they receive always matches that event's visibility
        settings.
      </Alert>

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Destination</TableCell>
                <TableCell>Events</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Auth</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Last delivery</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {integrations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No integrations yet. Click "Add integration" to wire up
                      the first client endpoint.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {integrations.map((row) => (
                <IntegrationRow
                  key={row.id}
                  eventId={eventId}
                  row={row}
                  onEdit={() => setEditingId(row.id)}
                  onViewDeliveries={() => setViewingDeliveriesFor(row)}
                  refetch={refetch}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {(creating || editingId) && (
        <IntegrationEditorDialog
          open
          eventId={eventId}
          existing={
            editingId
              ? integrations.find((x) => x.id === editingId) || null
              : null
          }
          roles={roles}
          onClose={() => {
            setCreating(false);
            setEditingId(null);
            refetch();
          }}
        />
      )}

      {viewingDeliveriesFor && (
        <DeliveriesDialog
          eventId={eventId}
          integration={viewingDeliveriesFor}
          onClose={() => setViewingDeliveriesFor(null)}
        />
      )}
    </Stack>
  );
}

function IntegrationRow({ eventId, row, onEdit, onViewDeliveries, refetch }) {
  const [toggle, { loading: toggling }] = useMutation(TOGGLE);
  const [del, { loading: deleting }] = useMutation(DELETE);
  const [testFire, { loading: firing }] = useMutation(TEST_FIRE);

  return (
    <TableRow hover>
      <TableCell>
        <Stack>
          <Typography variant="body2" fontWeight={600}>
            {row.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            id: {row.id}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell>
        <Tooltip title={row.destinationUrl}>
          <Typography variant="body2" sx={{ maxWidth: 240 }} noWrap>
            {safeHost(row.destinationUrl)}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {row.eventTypes.split(",").filter(Boolean).map((t) => (
            <Chip key={t} size="small" label={t} />
          ))}
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant="caption">
          {row.roles ? row.roles : <em>all roles</em>}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip size="small" label={row.authMethod} />
        {!row.hasSecret && (
          <Tooltip title="No secret stored">
            <Chip
              size="small"
              color="warning"
              label="no secret"
              sx={{ ml: 0.5 }}
            />
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        <Switch
          checked={row.enabled}
          disabled={toggling}
          onChange={(e) =>
            toggle({
              variables: {
                eventId,
                id: row.id,
                enabled: e.target.checked,
              },
            }).then(() => refetch())
          }
        />
      </TableCell>
      <TableCell>
        <DeliveryDot status={row.lastDeliveryStatus} at={row.lastDeliveryAt} />
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Tooltip title="Send test payload">
            <span>
              <IconButton
                size="small"
                disabled={firing}
                onClick={() =>
                  testFire({ variables: { eventId, id: row.id } })
                    .then((res) => {
                      alert(res.data?.sendIntegrationTestPayload?.message);
                      refetch();
                    })
                    .catch((e) => alert(e.message))
                }
              >
                <Icon icon="mdi:rocket-launch-outline" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="View deliveries">
            <IconButton size="small" onClick={onViewDeliveries}>
              <Icon icon="mdi:history" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={onEdit}>
              <Icon icon="mdi:pencil" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <span>
              <IconButton
                size="small"
                disabled={deleting}
                onClick={() => {
                  if (!window.confirm(`Delete integration "${row.name}"?`))
                    return;
                  del({ variables: { eventId, id: row.id } }).then(() =>
                    refetch()
                  );
                }}
              >
                <Icon icon="mdi:trash-can-outline" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function DeliveryDot({ status, at }) {
  if (!status) {
    return (
      <Typography variant="caption" color="text.secondary">
        none yet
      </Typography>
    );
  }
  const color =
    status === "delivered"
      ? "success"
      : status === "pending" || status === "in_flight"
      ? "info"
      : "error";
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Chip size="small" color={color} label={status} />
      {at && (
        <Typography variant="caption" color="text.secondary">
          {new Date(at).toLocaleString()}
        </Typography>
      )}
    </Stack>
  );
}

function safeHost(url) {
  try {
    return new URL(url).host;
  } catch (_) {
    return url;
  }
}

function IntegrationEditorDialog({ open, eventId, existing, roles, onClose }) {
  const isEdit = !!existing;
  const [form, setForm] = React.useState(() => ({
    name: existing?.name || "",
    destinationUrl: existing?.destinationUrl || "",
    eventTypes: existing?.eventTypes
      ? existing.eventTypes.split(",").filter(Boolean)
      : [],
    roles: existing?.roles ? existing.roles.split(",").filter(Boolean) : [],
    scanDirections: existing?.scanDirections
      ? existing.scanDirections.split(",").filter(Boolean)
      : ["Entry", "Exit"],
    authMethod: existing?.authMethod || "hmac",
    authHeaderName: existing?.authHeaderName || "",
    signatureAlgo: existing?.signatureAlgo || "sha256",
    authSecret: "",
    enabled: existing ? existing.enabled : true,
    maxAttempts: existing?.maxAttempts ?? 8,
    timeoutMs: existing?.timeoutMs ?? 10000,
  }));
  const [err, setErr] = React.useState(null);
  const [create, { loading: creating }] = useMutation(CREATE);
  const [update, { loading: updating }] = useMutation(UPDATE);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIn = (k, v) =>
    setForm((f) => ({
      ...f,
      [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v],
    }));

  const submit = async () => {
    setErr(null);
    const inp = {
      name: form.name.trim(),
      destinationUrl: form.destinationUrl.trim(),
      eventTypes: form.eventTypes.join(","),
      roles: form.roles.join(","),
      scanDirections: form.scanDirections.join(","),
      authMethod: form.authMethod,
      authHeaderName: form.authHeaderName.trim(),
      signatureAlgo: form.signatureAlgo,
      authSecret: form.authSecret || null,
      enabled: form.enabled,
      maxAttempts: Number(form.maxAttempts),
      timeoutMs: Number(form.timeoutMs),
    };
    try {
      if (isEdit) {
        await update({ variables: { eventId, id: existing.id, inp } });
      } else {
        await create({ variables: { eventId, inp } });
      }
      onClose();
    } catch (e) {
      setErr(e.message || "Save failed");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? "Edit integration" : "New integration"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {err && <Alert severity="error">{err}</Alert>}
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Destination URL (HTTPS only)"
            value={form.destinationUrl}
            onChange={(e) => set("destinationUrl", e.target.value)}
            fullWidth
            required
            placeholder="https://client.example.com/webhooks/eventegration"
          />

          <Divider>Event subscription</Divider>
          <FormGroup row>
            {EVENT_TYPES.map((t) => (
              <FormControlLabel
                key={t}
                control={
                  <Checkbox
                    checked={form.eventTypes.includes(t)}
                    onChange={() => toggleIn("eventTypes", t)}
                  />
                }
                label={t}
              />
            ))}
          </FormGroup>

          {form.eventTypes.includes("scan") && (
            <FormGroup row>
              <Typography variant="body2" sx={{ mr: 2, alignSelf: "center" }}>
                Scan directions:
              </Typography>
              {SCAN_DIRECTIONS.map((d) => (
                <FormControlLabel
                  key={d}
                  control={
                    <Checkbox
                      checked={form.scanDirections.includes(d)}
                      onChange={() => toggleIn("scanDirections", d)}
                    />
                  }
                  label={d}
                />
              ))}
            </FormGroup>
          )}

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Role filter (none = all roles):
            </Typography>
            <FormGroup row>
              {roles.map((r) => (
                <FormControlLabel
                  key={r}
                  control={
                    <Checkbox
                      checked={form.roles.includes(r)}
                      onChange={() => toggleIn("roles", r)}
                    />
                  }
                  label={r}
                />
              ))}
            </FormGroup>
          </Box>

          <Divider>Authentication</Divider>
          <FormControl fullWidth>
            <InputLabel>Auth method</InputLabel>
            <Select
              label="Auth method"
              value={form.authMethod}
              onChange={(e) => set("authMethod", e.target.value)}
            >
              {AUTH_METHODS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {form.authMethod === "api_key_header" && (
            <TextField
              label="Header name"
              value={form.authHeaderName}
              onChange={(e) => set("authHeaderName", e.target.value)}
              placeholder="X-ClientCo-Key"
              fullWidth
            />
          )}
          <TextField
            label={
              isEdit
                ? "Secret (leave blank to keep existing)"
                : "Secret"
            }
            type="password"
            value={form.authSecret}
            onChange={(e) => set("authSecret", e.target.value)}
            fullWidth
            helperText={
              isEdit && existing.hasSecret
                ? "An existing secret is stored. Type a new value to replace it."
                : "Stored plaintext for v1 (same precedent as payment + SMTP creds)."
            }
          />

          <Divider>Delivery</Divider>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Max attempts"
              type="number"
              value={form.maxAttempts}
              onChange={(e) => set("maxAttempts", e.target.value)}
              inputProps={{ min: 1, max: 20 }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Timeout (ms)"
              type="number"
              value={form.timeoutMs}
              onChange={(e) => set("timeoutMs", e.target.value)}
              inputProps={{ min: 1000, max: 30000 }}
              sx={{ flex: 1 }}
            />
          </Stack>
          <FormControlLabel
            control={
              <Switch
                checked={form.enabled}
                onChange={(e) => set("enabled", e.target.checked)}
              />
            }
            label="Enabled"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={submit}
          variant="contained"
          disabled={creating || updating}
        >
          {creating || updating ? <CircularProgress size={18} /> : isEdit ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeliveriesDialog({ eventId, integration, onClose }) {
  const [status, setStatus] = React.useState("");
  const [resourceId, setResourceId] = React.useState("");
  const { data, loading, refetch } = useQuery(DELIVERIES_QUERY, {
    variables: {
      eventId,
      integrationId: integration.id,
      status: status || null,
      resourceId: resourceId || null,
      limit: 200,
    },
    pollInterval: 3000,
  });
  const [replay] = useMutation(REPLAY);
  const [openRow, setOpenRow] = React.useState(null);

  const rows = data?.integrationDeliveries || [];
  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Deliveries — {integration.name}
        <Typography variant="caption" sx={{ display: "block" }}>
          {safeHost(integration.destinationUrl)}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={2} sx={{ pb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">pending</MenuItem>
              <MenuItem value="in_flight">in_flight</MenuItem>
              <MenuItem value="delivered">delivered</MenuItem>
              <MenuItem value="failed">failed</MenuItem>
              <MenuItem value="dead_letter">dead_letter</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Attendee / resource id"
            value={resourceId}
            onChange={(e) => setResourceId(e.target.value)}
            sx={{ minWidth: 240 }}
          />
        </Stack>
        {loading && !data && <LinearProgress />}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Attempts</TableCell>
                <TableCell>HTTP</TableCell>
                <TableCell>Last error</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography
                      color="text.secondary"
                      sx={{ py: 2 }}
                      align="center"
                    >
                      No deliveries yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((d) => (
                <TableRow key={d.id} hover onClick={() => setOpenRow(d)}>
                  <TableCell>
                    {new Date(d.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>{d.eventType}</TableCell>
                  <TableCell>{d.role || "—"}</TableCell>
                  <TableCell>
                    <Typography variant="caption">{d.resourceId}</Typography>
                  </TableCell>
                  <TableCell>
                    <DeliveryDot status={d.status} at={d.lastAttemptAt || d.createdAt} />
                  </TableCell>
                  <TableCell>{d.attempts}</TableCell>
                  <TableCell>{d.lastStatusCode || "—"}</TableCell>
                  <TableCell>
                    <Tooltip title={d.lastError || ""}>
                      <Typography variant="caption" noWrap sx={{ maxWidth: 200, display: "inline-block" }}>
                        {d.lastError || ""}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {(d.status === "failed" || d.status === "dead_letter") && (
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          replay({
                            variables: { eventId, deliveryId: d.id },
                          }).then(() => refetch());
                        }}
                      >
                        Replay
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {openRow && (
          <DeliveryDetailDialog row={openRow} onClose={() => setOpenRow(null)} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function DeliveryDetailDialog({ row, onClose }) {
  let pretty = row.payloadJson;
  try {
    pretty = JSON.stringify(JSON.parse(row.payloadJson), null, 2);
  } catch (_) {}
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Delivery {row.id}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="subtitle2">Payload</Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: "#0b1020",
              color: "#e2e8f0",
              p: 2,
              borderRadius: 1,
              maxHeight: 320,
              overflow: "auto",
              fontSize: 12,
            }}
          >
            {pretty}
          </Box>
          <Typography variant="subtitle2">Last response excerpt</Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: "#f6f8fa",
              p: 2,
              borderRadius: 1,
              maxHeight: 200,
              overflow: "auto",
              fontSize: 12,
            }}
          >
            {row.lastResponseExcerpt || "(none)"}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
